import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import * as Recharts from 'recharts';
import * as echarts from 'echarts/core';
import { TreeChart } from 'echarts/charts';
import { TooltipComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { BOOL_FACE_ATTRS, fmtTileRC } from '../helpers';
import style from '../style.module.scss';
import {
  ATTR_COLOR,
  ATTR_KEYS,
  ATTR_LABELS,
  LABEL_BOX_COLOR,
  buildLabelCatalog,
  buildPersonDemoTrees,
  emotionColor,
  formatPct,
  openLabelDetail,
  titleCase,
} from './data';

const AXIS_TICK = { fontSize: 11, fill: '#4E5155' };
const RECHART_TOOLTIP_STYLE = {
  borderRadius: 8,
  border: '1px solid #E8E8E8',
  boxShadow: '0 6px 20px rgba(135, 135, 136, 0.14)',
  fontSize: 12,
};
const CONF_COLORS = {
  face: '#4E9BF6',
  person: '#FFAE20',
  gender: '#7E8ED9',
};

const RADIAN = Math.PI / 180;

const renderPieLabel = ({
  cx, cy, midAngle, outerRadius, percent, name, value,
}) => {
  if (!percent || percent < 0.03) return null;
  const radius = outerRadius + 22;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text
      x={x}
      y={y}
      fill="#4E5155"
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
      fontSize={12}
      fontWeight={500}
    >
      {`${name} ${value} (${Math.round(percent * 100)}%)`}
    </text>
  );
};

const Section = ({ title, children, className }) => (
  <section className={[style.section, className].filter(Boolean).join(' ')}>
    {title ? <h5 className={style.sectionTitle}>{title}</h5> : null}
    {children}
  </section>
);

const StatStrip = ({ items }) => (
  <div className={style.statStrip}>
    {items.map(({
      label, value, icon, tone,
    }) => (
      <div
        key={label}
        className={[style.statCard, tone && style[`statCard_${tone}`]].filter(Boolean).join(' ')}
      >
        {icon ? (
          <div className={style.statIcon} aria-hidden>
            <FontAwesomeIcon icon={icon} />
          </div>
        ) : null}
        <div className={style.statBody}>
          <div className={style.statValue}>{value}</div>
          <div className={style.statLabel}>{label}</div>
        </div>
      </div>
    ))}
  </div>
);

const ChartCard = ({
  title, meta, actions, children, className, printBreak,
}) => (
  <div
    className={[
      style.chartCard,
      printBreak ? 'print-break-page' : null,
      className,
    ].filter(Boolean).join(' ')}
  >
    {(title || meta || actions) ? (
      <div className={style.chartHeader}>
        {title ? <span className={style.chartTitle}>{title}</span> : <span />}
        <div className={style.chartHeaderRight}>
          {meta ? <span className={style.chartMeta}>{meta}</span> : null}
          {actions}
        </div>
      </div>
    ) : null}
    {children}
  </div>
);

const formatCountTick = (v) => {
  if (v >= 10000) return `${Math.round(v / 1000)}k`;
  if (v >= 1000) return `${(v / 1000).toFixed(1).replace(/\.0$/, '')}k`;
  return String(v);
};

const YScaleToggle = ({ value, onChange }) => (
  <div className={style.scaleToggle} role="group" aria-label="Y axis scale">
    <button
      type="button"
      className={value === 'log' ? style.scaleToggleActive : undefined}
      onClick={() => onChange('log')}
    >
      Log
    </button>
    <button
      type="button"
      className={value === 'linear' ? style.scaleToggleActive : undefined}
      onClick={() => onChange('linear')}
    >
      Linear
    </button>
  </div>
);

const logYAxisProps = {
  scale: 'log',
  domain: [1, (dataMax) => Math.max(Number(dataMax) || 1, 1)],
  allowDataOverflow: true,
  tickFormatter: formatCountTick,
};

const linearYAxisProps = {
  allowDecimals: false,
  tickFormatter: formatCountTick,
};

const binsToSeries = (bins) => bins.map((count, pct) => ({
  pct,
  count,
  // Log can't use 0 — floor empty bins so the curve stays continuous from the left.
  y: Math.max(count, 1),
}));

let confidenceGradientSeq = 0;

const ConfidenceChart = ({ title, bins, color }) => {
  confidenceGradientSeq += 1;
  const gid = useRef(`g-${confidenceGradientSeq}`).current;
  const [yScale, setYScale] = useState('log');
  const data = useMemo(() => binsToSeries(bins), [bins]);
  const total = bins.reduce((sum, n) => sum + n, 0);
  const hasData = total > 0;
  const isLog = yScale === 'log';
  const yAxisProps = isLog ? logYAxisProps : linearYAxisProps;

  return (
    <ChartCard
      title={title}
      actions={hasData ? <YScaleToggle value={yScale} onChange={setYScale} /> : null}
    >
      {!hasData ? (
        <div className={style.emptyChart}>No data</div>
      ) : (
        <div className={style.rechartWrap}>
          <Recharts.ResponsiveContainer width="100%" height={200}>
            <Recharts.AreaChart
              data={data}
              margin={{
                top: 8, right: 8, left: 0, bottom: 0,
              }}
            >
              <defs>
                <linearGradient id={`conf-${gid}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity={0.45} />
                  <stop offset="100%" stopColor={color} stopOpacity={0.04} />
                </linearGradient>
              </defs>
              <Recharts.CartesianGrid strokeDasharray="3 3" stroke="#E8E8E8" vertical={false} />
              <Recharts.XAxis
                dataKey="pct"
                type="number"
                domain={[0, 99]}
                ticks={[0, 25, 50, 75, 99]}
                tickFormatter={(v) => (v === 99 ? '100' : String(v))}
                tick={AXIS_TICK}
                axisLine={{ stroke: '#B7B8BA' }}
                tickLine
              />
              <Recharts.YAxis
                width={42}
                tick={AXIS_TICK}
                axisLine={{ stroke: '#B7B8BA' }}
                tickLine
                {...yAxisProps}
              />
              <Recharts.Tooltip
                cursor={{ stroke: 'rgba(78, 81, 85, 0.25)', strokeWidth: 1 }}
                contentStyle={RECHART_TOOLTIP_STYLE}
                labelFormatter={(pct) => `Confidence ${pct}%`}
                formatter={(_value, _name, item) => [item?.payload?.count ?? 0, 'Count']}
              />
              <Recharts.Area
                type="monotone"
                dataKey={isLog ? 'y' : 'count'}
                stroke={color}
                strokeWidth={2}
                fill={`url(#conf-${gid})`}
                baseValue={isLog ? 1 : 0}
                connectNulls
                isAnimationActive={false}
              />
            </Recharts.AreaChart>
          </Recharts.ResponsiveContainer>
        </div>
      )}
    </ChartCard>
  );
};

const DistributionPie = ({ title, slices }) => {
  const total = slices.reduce((sum, s) => sum + s.value, 0);
  const hasData = total > 0;

  return (
    <ChartCard title={title}>
      {!hasData ? (
        <div className={style.emptyChart}>No age data</div>
      ) : (
        <div className={style.rechartWrap}>
          <Recharts.ResponsiveContainer width="100%" height={320}>
            <Recharts.PieChart margin={{
              top: 12, right: 16, bottom: 8, left: 16,
            }}
            >
              <Recharts.Pie
                data={slices}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={48}
                outerRadius={82}
                paddingAngle={2}
                isAnimationActive={false}
                label={renderPieLabel}
                labelLine={{ stroke: '#B7B8BA', strokeWidth: 1 }}
              >
                {slices.map((slice) => (
                  <Recharts.Cell key={slice.name} fill={slice.fill} />
                ))}
              </Recharts.Pie>
              <Recharts.Tooltip
                contentStyle={RECHART_TOOLTIP_STYLE}
                formatter={(value, name) => [value, name]}
              />
              <Recharts.Legend
                iconType="circle"
                iconSize={10}
                wrapperStyle={{ fontSize: '13px', color: '#4E5155' }}
              />
            </Recharts.PieChart>
          </Recharts.ResponsiveContainer>
        </div>
      )}
    </ChartCard>
  );
};


const AgeHistogram = ({ title, rows, printBreak }) => {
  const total = rows.reduce((sum, r) => sum + r.value, 0);
  const hasData = total > 0;
  const many = rows.length > 10;
  const height = many ? Math.min(360, 220 + Math.min(rows.length, 20) * 4) : 260;

  return (
    <ChartCard title={title} printBreak={printBreak}>
      {!hasData ? (
        <div className={style.emptyChart}>No age data</div>
      ) : (
        <div className={style.rechartWrap}>
          <Recharts.ResponsiveContainer width="100%" height={height}>
            <Recharts.BarChart
              data={rows}
              margin={{
                top: 8, right: 16, bottom: many ? 48 : 8, left: 8,
              }}
            >
              <Recharts.CartesianGrid strokeDasharray="3 3" stroke="#E8E8E8" vertical={false} />
              <Recharts.XAxis
                dataKey="name"
                tick={AXIS_TICK}
                axisLine={{ stroke: '#B7B8BA' }}
                tickLine
                interval={many ? 'preserveStartEnd' : 0}
                angle={many ? -35 : 0}
                textAnchor={many ? 'end' : 'middle'}
                height={many ? 60 : 30}
              />
              <Recharts.YAxis
                width={44}
                tick={AXIS_TICK}
                axisLine={{ stroke: '#B7B8BA' }}
                tickLine
                label={{
                  value: 'Count',
                  angle: -90,
                  position: 'insideLeft',
                  style: { fill: '#4E5155', fontSize: 12, textAnchor: 'middle' },
                }}
                {...linearYAxisProps}
              />
              <Recharts.Tooltip
                cursor={{ fill: 'rgba(78, 81, 85, 0.06)' }}
                contentStyle={RECHART_TOOLTIP_STYLE}
                formatter={(value) => [value, 'Faces']}
              />
              <Recharts.Bar dataKey="value" name="Faces" radius={[10, 10, 0, 0]} isAnimationActive={false}>
                {rows.map((row) => (
                  <Recharts.Cell key={row.name} fill={row.fill} />
                ))}
              </Recharts.Bar>
            </Recharts.BarChart>
          </Recharts.ResponsiveContainer>
        </div>
      )}
    </ChartCard>
  );
};

const RANK_Y_WIDTH = 120;
const RANK_ROW_PX = 36;
const RANK_LABEL_RIGHT = 88;

const RankEndLabel = ({
  x, y, width, height, viewBox, endPct, endCount, value,
}) => {
  const pct = endPct ?? (typeof value === 'string' ? value : value?.pct);
  const count = endCount ?? (typeof value === 'object' && value != null ? value.count : null);
  if (pct == null && count == null) return null;
  const bx = Number(x ?? viewBox?.x);
  const by = Number(y ?? viewBox?.y);
  const bw = Number(width ?? viewBox?.width ?? 0);
  const bh = Number(height ?? viewBox?.height ?? 0);
  const px = bx + bw + 8;
  const py = by + bh / 2;
  if (!Number.isFinite(px) || !Number.isFinite(py)) return null;
  const label = [pct, count].filter((v) => v != null).join(' ');
  return (
    <text
      x={px}
      y={py}
      textAnchor="start"
      dominantBaseline="central"
      fill="#4E5155"
      fontSize={11}
      fontWeight={600}
      style={{ pointerEvents: 'none' }}
    >
      {label}
    </text>
  );
};

const RankBarChart = ({
  rows,
  dataKey = 'value',
  seriesName = 'Count',
  formatter,
  onBarClick,
  emptyText = 'No data',
  xDomain,
  xTickFormatter,
  stickyAxis = false,
}) => {
  const height = Math.max(200, 24 + rows.length * RANK_ROW_PX);
  const hasEndLabels = rows.some((r) => r.endPct != null || r.endCount != null);
  const rightPad = hasEndLabels ? RANK_LABEL_RIGHT : 24;
  const dataMax = Math.max(0, ...rows.map((r) => Number(r[dataKey]) || 0));
  const axisDomain = xDomain || [0, Math.max(dataMax, 1)];

  if (!rows.length) {
    return <div className={style.emptyChart}>{emptyText}</div>;
  }

  const sharedMargin = {
    top: 8,
    right: rightPad,
    bottom: stickyAxis ? 4 : 8,
    left: 8,
  };

  const renderEndLabel = (props) => {
    const row = rows[props.index];
    return (
      <RankEndLabel
        {...props}
        endPct={row?.endPct}
        endCount={row?.endCount}
      />
    );
  };

  const bars = (
    <Recharts.Bar
      dataKey={dataKey}
      name={seriesName}
      radius={[0, 10, 10, 0]}
      minPointSize={6}
      isAnimationActive={false}
      cursor={onBarClick ? 'pointer' : 'default'}
      onClick={onBarClick ? (item) => onBarClick(item?.payload || item) : undefined}
    >
      {rows.map((row) => (
        <Recharts.Cell key={row.name} fill={row.fill} />
      ))}
      {hasEndLabels ? (
        <Recharts.LabelList dataKey="endPct" content={renderEndLabel} />
      ) : null}
    </Recharts.Bar>
  );

  if (!stickyAxis) {
    return (
      <div className={style.rechartWrap} style={{ height }}>
        <Recharts.ResponsiveContainer width="100%" height={height}>
          <Recharts.BarChart layout="vertical" data={rows} margin={sharedMargin}>
            <Recharts.CartesianGrid strokeDasharray="3 3" stroke="#E8E8E8" horizontal={false} />
            <Recharts.XAxis
              type="number"
              tick={AXIS_TICK}
              axisLine={{ stroke: '#B7B8BA' }}
              tickLine
              domain={axisDomain}
              tickFormatter={xTickFormatter}
              allowDecimals={false}
            />
            <Recharts.YAxis
              type="category"
              dataKey="name"
              width={RANK_Y_WIDTH}
              tick={AXIS_TICK}
              axisLine={{ stroke: '#B7B8BA' }}
              tickLine
              interval={0}
            />
            <Recharts.Tooltip
              cursor={{ fill: 'rgba(78, 81, 85, 0.06)' }}
              contentStyle={RECHART_TOOLTIP_STYLE}
              formatter={formatter || ((value) => [value, seriesName])}
            />
            {bars}
          </Recharts.BarChart>
        </Recharts.ResponsiveContainer>
      </div>
    );
  }

  return (
    <div className={style.rankBarShell}>
      <div className={style.rankBarScroll}>
        <div className={style.rechartWrap} style={{ height }}>
          <Recharts.ResponsiveContainer width="100%" height={height}>
            <Recharts.BarChart layout="vertical" data={rows} margin={sharedMargin}>
              <Recharts.CartesianGrid strokeDasharray="3 3" stroke="#E8E8E8" horizontal={false} />
              <Recharts.XAxis type="number" hide domain={axisDomain} />
              <Recharts.YAxis
                type="category"
                dataKey="name"
                width={RANK_Y_WIDTH}
                tick={AXIS_TICK}
                axisLine={{ stroke: '#B7B8BA' }}
                tickLine
                interval={0}
              />
              <Recharts.Tooltip
                cursor={{ fill: 'rgba(78, 81, 85, 0.06)' }}
                contentStyle={RECHART_TOOLTIP_STYLE}
                formatter={formatter || ((value) => [value, seriesName])}
              />
              {bars}
            </Recharts.BarChart>
          </Recharts.ResponsiveContainer>
        </div>
      </div>
      <div className={style.rankBarAxisFooter}>
        <Recharts.ResponsiveContainer width="100%" height={40}>
          <Recharts.BarChart
            data={[{ [dataKey]: dataMax }]}
            margin={{
              top: 8, right: rightPad, bottom: 0, left: 8 + RANK_Y_WIDTH,
            }}
          >
            <Recharts.XAxis
              type="number"
              dataKey={dataKey}
              domain={axisDomain}
              tick={AXIS_TICK}
              axisLine={{ stroke: '#B7B8BA' }}
              tickLine
              tickFormatter={xTickFormatter}
              allowDecimals={false}
            />
          </Recharts.BarChart>
        </Recharts.ResponsiveContainer>
      </div>
    </div>
  );
};

const EmotionsChart = ({ entries }) => {
  const total = entries.reduce((sum, [, n]) => sum + n, 0);
  const rows = entries.map(([name, count]) => ({
    name: titleCase(name),
    value: count,
    fill: emotionColor(name),
    endPct: formatPct(count, total),
    endCount: count,
  }));

  return (
    <ChartCard title="Emotion Distribution" meta={total ? `${total} faces` : null}>
      <RankBarChart
        rows={rows}
        seriesName="Faces"
        emptyText="No emotions"
        formatter={(value) => [`${value} (${formatPct(value, total)})`, 'Faces']}
      />
    </ChartCard>
  );
};

const FaceAttributesChart = ({ faces }) => {
  const rows = BOOL_FACE_ATTRS.map((name) => {
    const key = ATTR_KEYS[name];
    let present = 0;
    let known = 0;
    faces.forEach((f) => {
      const v = f[key];
      if (v === true) {
        present += 1;
        known += 1;
      } else if (v === false) {
        known += 1;
      }
    });
    return {
      name: ATTR_LABELS[name] || name,
      value: known ? Math.round((present / known) * 1000) / 10 : 0,
      present,
      known,
      rate: known ? present / known : 0,
      fill: ATTR_COLOR,
      endPct: formatPct(present, known),
      endCount: present,
    };
  })
    .filter((r) => r.known > 0)
    .sort((a, b) => b.rate - a.rate || a.name.localeCompare(b.name));

  return (
    <ChartCard
      title="Face Attributes"
      meta={faces.length ? `${faces.length} faces` : null}
    >
      <RankBarChart
        rows={rows}
        seriesName="Rate"
        emptyText="No attributes"
        xDomain={[0, 100]}
        xTickFormatter={(v) => `${v}%`}
        formatter={(_value, _name, item) => {
          const row = item?.payload;
          if (!row) return [_value, 'Rate'];
          return [
            `${row.present} of ${row.known} (${formatPct(row.present, row.known)})`,
            'Present',
          ];
        }}
      />
    </ChartCard>
  );
};

const COVERAGE_STOPS = ['#8EBFEC', '#6AABE4', '#5A9FE3', '#3F88CC', '#2F6FB5', '#1A406E'];

const coverageFill = (count, max, stops) => {
  if (!count || !max) return stops[0];
  const t = Math.min(1, count / max);
  const idx = Math.min(stops.length - 1, Math.round(t * (stops.length - 1)));
  return stops[idx];
};

const TileHeatmap = ({
  title, grid, rows, cols, stops, onCellClick, printBreak,
}) => {
  const flat = grid.flat();
  const max = Math.max(1, ...flat);
  const total = flat.reduce((s, n) => s + n, 0);
  const palette = stops || COVERAGE_STOPS;
  const cells = [];

  for (let r = 0; r < rows; r += 1) {
    for (let c = 0; c < cols; c += 1) {
      const count = grid[r]?.[c] || 0;
      const bg = coverageFill(count, max, palette);
      cells.push(
        <button
          type="button"
          key={`r${r}-c${c}`}
          className={style.heatmapCell}
          style={{ background: bg }}
          title={fmtTileRC(r, c)}
          aria-label={fmtTileRC(r, c)}
          onClick={() => onCellClick?.(r, c)}
        >
          <span className={style.heatmapCount}>{count}</span>
        </button>,
      );
    }
  }

  const meta = total ? `${rows}×${cols} · ${total} total` : `${rows}×${cols}`;

  return (
    <ChartCard title={title} meta={meta} printBreak={printBreak}>
      {!rows || !cols ? (
        <div className={style.emptyChart}>No tile grid</div>
      ) : (
        <div
          className={style.heatmap}
          style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
        >
          {cells}
        </div>
      )}
    </ChartCard>
  );
};

echarts.use([TreeChart, TooltipComponent, CanvasRenderer]);

const PersonHierarchyTree = ({ catalog }) => {
  const demo = useMemo(() => buildPersonDemoTrees(catalog), [catalog]);
  const ageRef = useRef(null);
  const genderRef = useRef(null);

  useEffect(() => {
    if (!ageRef.current || !genderRef.current || !demo) return undefined;

    const treeOpts = (root) => ({
      animationDuration: 350,
      tooltip: {
        trigger: 'item',
        formatter: (p) => {
          const pct = p.data?.pct;
          const count = p.data?.count;
          const parent = p.treeAncestors?.[p.treeAncestors.length - 2]?.name;
          const lines = [`<b>${p.name}</b>`];
          if (count != null) lines.push(String(count));
          if (pct != null && parent && p.name !== root.name) {
            lines.push(`${pct}% of ${parent}`);
          }
          return lines.join('<br/>');
        },
      },
      series: [{
        type: 'tree',
        data: [root],
        top: '6%',
        left: '12%',
        bottom: '6%',
        right: '28%',
        orient: 'LR',
        symbol: 'circle',
        symbolSize: 12,
        expandAndCollapse: true,
        initialTreeDepth: 2,
        roam: true,
        scaleLimit: { min: 0.3, max: 4 },
        label: {
          position: 'right',
          verticalAlign: 'middle',
          align: 'left',
          fontSize: 12,
          color: '#1f2328',
          backgroundColor: 'rgba(255,255,255,0.92)',
          padding: [2, 6],
          borderRadius: 3,
        },
        lineStyle: { color: '#c5ccd6', width: 1.5, curveness: 0.4 },
        itemStyle: { borderColor: '#fff', borderWidth: 2 },
        emphasis: {
          focus: 'descendant',
          lineStyle: { width: 2.5, color: '#4E9BF6' },
          label: { fontWeight: 700 },
        },
      }],
    });

    const ageChart = echarts.init(ageRef.current);
    const genderChart = echarts.init(genderRef.current);
    ageChart.setOption(treeOpts(demo.ageRoot));
    genderChart.setOption(treeOpts(demo.genderRoot));

    const onResize = () => {
      ageChart.resize();
      genderChart.resize();
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      ageChart.dispose();
      genderChart.dispose();
    };
  }, [demo]);

  if (!demo?.personCount) {
    return (
      <ChartCard title="Crowd Composition" className={style.sectionSpaced}>
        <div className={style.emptyChart}>No Person labels</div>
      </ChartCard>
    );
  }

  return (
    <ChartCard title="Crowd Composition" className={style.sectionSpaced}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: '#4E5155',
              marginBottom: 4,
            }}
          >
            Age
          </div>
          <div ref={ageRef} style={{ width: '100%', height: 360 }} />
        </div>
        <div>
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: '#4E5155',
              marginBottom: 4,
            }}
          >
            Gender
          </div>
          <div ref={genderRef} style={{ width: '100%', height: 360 }} />
        </div>
      </div>
    </ChartCard>
  );
};

const LabelsExplorer = ({ tileBoxes, observations, onOpenDetail }) => {
  const boxed = useMemo(
    () => buildLabelCatalog(tileBoxes, observations),
    [tileBoxes, observations],
  );
  const boxedTotal = boxed.reduce((s, e) => s + e.count, 0);
  const rows = boxed.filter((e) => e.count > 0).map((entry) => ({
    name: entry.name,
    value: entry.count,
    fill: LABEL_BOX_COLOR,
    entry,
    endPct: formatPct(entry.count, boxedTotal),
    endCount: entry.count,
  }));

  if (!rows.length) {
    return (
      <ChartCard title="Label Distribution">
        <div className={style.emptyChart}>No labels</div>
      </ChartCard>
    );
  }

  return (
    <ChartCard
      title="Label Distribution"
      meta={`${rows.length} labels · ${boxedTotal} boxes`}
    >
      <RankBarChart
        rows={rows}
        seriesName="Boxes"
        stickyAxis
        formatter={(value) => [`${value} (${formatPct(value, boxedTotal)})`, 'Boxes']}
        onBarClick={(payload) => openLabelDetail(payload?.entry, onOpenDetail)}
      />
    </ChartCard>
  );
};


export {
  Section,
  StatStrip,
  ChartCard,
  ConfidenceChart,
  DistributionPie,
  AgeHistogram,
  EmotionsChart,
  FaceAttributesChart,
  TileHeatmap,
  PersonHierarchyTree,
  LabelsExplorer,
  CONF_COLORS,
  COVERAGE_STOPS,
};
